import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { ARTHA_BIDHA } from '../constants/arthaEnums';
import { SUBANTA_SHRENI, enumToOptions } from '../constants/subantaEnums';
import { listLanguages, type Language } from '../api/languageApi';
import { listUpasargas, type Upasarga } from '../api/upasargaApi';
import {
  createTinganta,
  deleteTinganta,
  getTinganta,
  listTingantas,
  normalizeTingantaScalars,
  updateTinganta,
  TINGANTA_SCALAR_KEYS,
  type Tinganta,
  type TingantaScalars,
} from '../api/tingantaApi';
import {
  buildTingantaArthaPatch,
  createTingantaArtha,
  deleteTingantaArtha,
  listTingantaArthas,
  parseTingantaArthas,
  updateTingantaArtha,
  type TingantaArtha,
} from '../api/tingantaArthaApi';
import {
  buildUpasargaArthaPatch,
  createUpasargaArtha,
  deleteUpasargaArtha,
  listUpasargaArthas,
  parseUpasargaArthas,
  updateUpasargaArtha,
  type UpasargaArtha,
  type UpasargaArthaFormValues,
} from '../api/upasargaArthaApi';
import { EntryNotificationsPanel } from '../components/EntryNotificationsPanel';
import { EntryMediaPanel } from '../components/EntryMediaPanel';
import { EntryStatsPanel } from '../components/EntryStatsPanel';
import { TingantaRupSections } from '../components/TingantaRupSections';

type TingantaArthaFormValues = {
  languageId: number;
  text: string;
};

function formatUpasargaIdList(
  ids: number[],
  upasargas: Upasarga[]
): string {
  if (ids.length === 0) return '—';
  return ids
    .map((id) => {
      const u = upasargas.find((x) => x.id === id);
      return u ? `${u.name} (#${id})` : `#${id}`;
    })
    .join(' · ');
}

function buildPatch(prev: Tinganta, next: TingantaScalars): Partial<TingantaScalars> {
  const patch: Partial<TingantaScalars> = {};
  for (const key of TINGANTA_SCALAR_KEYS) {
    if (prev[key] !== next[key]) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}

function scalarsFromRecord(record: Tinganta): TingantaScalars {
  return normalizeTingantaScalars(record);
}

export function TingantasPage() {
  const [rows, setRows] = useState<Tinganta[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tinganta | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Tinganta | null>(null);
  const [form] = Form.useForm<TingantaScalars>();
  const [languageRows, setLanguageRows] = useState<Language[]>([]);
  const [tingantaArthaRows, setTingantaArthaRows] = useState<TingantaArtha[]>([]);
  const [tingantaArthasLoading, setTingantaArthasLoading] = useState(false);
  const [taModalOpen, setTaModalOpen] = useState(false);
  const [taEditing, setTaEditing] = useState<TingantaArtha | null>(null);
  const [taHeadTingantaId, setTaHeadTingantaId] = useState<number | null>(null);
  const [taModalSaving, setTaModalSaving] = useState(false);
  const [taForm] = Form.useForm<TingantaArthaFormValues>();
  const [upasargaList, setUpasargaList] = useState<Upasarga[]>([]);
  const [upasargaArthaRows, setUpasargaArthaRows] = useState<UpasargaArtha[]>([]);
  const [upasargaArthasLoading, setUpasargaArthasLoading] = useState(false);
  const [uaModalOpen, setUaModalOpen] = useState(false);
  const [uaEditing, setUaEditing] = useState<UpasargaArtha | null>(null);
  const [uaHeadTingantaId, setUaHeadTingantaId] = useState<number | null>(null);
  const [uaModalSaving, setUaModalSaving] = useState(false);
  const [uaForm] = Form.useForm<UpasargaArthaFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTingantas();
      setRows(data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tingantas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void listLanguages()
      .then(setLanguageRows)
      .catch((err: unknown) => {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load languages');
      });
  }, []);

  useEffect(() => {
    void listUpasargas()
      .then(setUpasargaList)
      .catch((err: unknown) => {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load upasargas');
      });
  }, []);

  useEffect(() => {
    if (!detail) {
      setTingantaArthaRows([]);
      setTingantaArthasLoading(false);
      return;
    }
    const embedded = parseTingantaArthas(detail.tingantaArthas).filter(
      (a) => a.tingantaId === detail.id
    );
    if (embedded.length > 0) {
      setTingantaArthaRows(embedded);
      setTingantaArthasLoading(false);
      return;
    }
    let cancelled = false;
    setTingantaArthasLoading(true);
    void listTingantaArthas(detail.id)
      .then((rows) => {
        if (!cancelled) setTingantaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load tinganta arthas'
          );
          setTingantaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTingantaArthasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail) {
      setUpasargaArthaRows([]);
      setUpasargaArthasLoading(false);
      return;
    }
    const embedded = parseUpasargaArthas(detail.upasargaArthas as unknown[] | undefined).filter(
      (a) => a.tingantaId === detail.id
    );
    if (embedded.length > 0) {
      setUpasargaArthaRows(embedded);
      setUpasargaArthasLoading(false);
      return;
    }
    let cancelledUa = false;
    setUpasargaArthasLoading(true);
    void listUpasargaArthas(detail.id)
      .then((rows) => {
        if (!cancelledUa) setUpasargaArthaRows(rows);
      })
      .catch((err: unknown) => {
        if (!cancelledUa) {
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.error(
            ax?.response?.data?.message || ax?.message || 'Failed to load upasarga arthas'
          );
          setUpasargaArthaRows([]);
        }
      })
      .finally(() => {
        if (!cancelledUa) setUpasargaArthasLoading(false);
      });
    return () => {
      cancelledUa = true;
    };
  }, [detail]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(normalizeTingantaScalars({}));
    setModalOpen(true);
  };

  const openEdit = (record: Tinganta) => {
    setEditing(record);
    form.setFieldsValue(scalarsFromRecord(record));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const scalars = normalizeTingantaScalars(values);
      setSaving(true);
      if (editing) {
        const patch = buildPatch(editing, scalars);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateTinganta(editing.id, patch);
        message.success('Tinganta updated');
      } else {
        await createTinganta(scalars);
        message.success('Tinganta created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      const row = await getTinganta(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tinganta');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTinganta(id);
      message.success('Tinganta deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const reloadDetailIfOpen = useCallback(async () => {
    const id = detail?.id;
    if (id == null) return;
    try {
      const row = await getTinganta(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to refresh tinganta');
    }
  }, [detail?.id]);

  const closeTaModal = useCallback(() => {
    setTaModalOpen(false);
    setTaEditing(null);
    setTaHeadTingantaId(null);
    taForm.resetFields();
  }, [taForm]);

  const openTaCreate = useCallback(
    (tingantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Add languages on the Languages page first.');
        return;
      }
      setTaEditing(null);
      setTaHeadTingantaId(tingantaId);
      taForm.resetFields();
      taForm.setFieldsValue({
        languageId: languageRows[0].id,
        text: '',
      });
      setTaModalOpen(true);
    },
    [languageRows, taForm]
  );

  const openTaEdit = useCallback(
    (row: TingantaArtha) => {
      setTaEditing(row);
      setTaHeadTingantaId(row.tingantaId);
      taForm.setFieldsValue({
        languageId: row.languageId,
        text: row.text,
      });
      setTaModalOpen(true);
    },
    [taForm]
  );

  const submitTaModal = useCallback(async () => {
    try {
      const values = await taForm.validateFields();
      const headId = taHeadTingantaId;
      if (headId == null) return;
      setTaModalSaving(true);
      const next = { languageId: values.languageId, text: values.text.trim() };
      if (taEditing) {
        const patch = buildTingantaArthaPatch(taEditing, next);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeTaModal();
          return;
        }
        await updateTingantaArtha(taEditing.id, patch);
        message.success('Tinganta artha updated');
      } else {
        await createTingantaArtha({
          tingantaId: headId,
          languageId: next.languageId,
          text: next.text,
        });
        message.success('Tinganta artha created');
      }
      closeTaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setTaModalSaving(false);
    }
  }, [closeTaModal, reloadDetailIfOpen, taEditing, taForm, taHeadTingantaId]);

  const handleDeleteTingantaArtha = useCallback(
    async (id: number) => {
      try {
        await deleteTingantaArtha(id);
        message.success('Tinganta artha deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const closeUaModal = useCallback(() => {
    setUaModalOpen(false);
    setUaEditing(null);
    setUaHeadTingantaId(null);
    uaForm.resetFields();
  }, [uaForm]);

  const openUaCreate = useCallback(
    (tingantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Add languages on the Languages page first.');
        return;
      }
      if (upasargaList.length === 0) {
        message.warning('No upasargas loaded. Add upasargas on the Upasarga page first.');
        return;
      }
      setUaEditing(null);
      setUaHeadTingantaId(tingantaId);
      uaForm.resetFields();
      uaForm.setFieldsValue({
        languageId: languageRows[0].id,
        artha: '',
        upasargaIds: [],
        example: '',
        antonyms: '',
        synonyms: '',
        tags: '',
      });
      setUaModalOpen(true);
    },
    [languageRows, upasargaList.length, uaForm]
  );

  const openUaEdit = useCallback(
    (row: UpasargaArtha) => {
      setUaEditing(row);
      setUaHeadTingantaId(row.tingantaId);
      uaForm.setFieldsValue({
        languageId: row.languageId,
        artha: row.artha,
        upasargaIds: [...row.upasargaIds],
        example: row.example ?? '',
        antonyms: row.antonyms ?? '',
        synonyms: row.synonyms ?? '',
        tags: row.tags ?? '',
        bidha: row.bidha ?? undefined,
        prakar: row.prakar ?? undefined,
      });
      setUaModalOpen(true);
    },
    [uaForm]
  );

  const submitUaModal = useCallback(async () => {
    try {
      const values = await uaForm.validateFields();
      const headId = uaHeadTingantaId;
      if (headId == null) return;
      setUaModalSaving(true);
      if (uaEditing) {
        const patch = buildUpasargaArthaPatch(uaEditing, values);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeUaModal();
          return;
        }
        await updateUpasargaArtha(uaEditing.id, patch);
        message.success('Upasarga artha updated');
      } else {
        const optStr = (s: string | undefined): string | undefined => {
          const t = (s ?? '').trim();
          return t === '' ? undefined : t;
        };
        await createUpasargaArtha({
          tingantaId: headId,
          languageId: values.languageId,
          artha: (values.artha ?? '').trim(),
          upasargaIds: values.upasargaIds,
          example: optStr(values.example),
          antonyms: optStr(values.antonyms),
          synonyms: optStr(values.synonyms),
          tags: optStr(values.tags),
          bidha: values.bidha ?? undefined,
          prakar: values.prakar ?? undefined,
        });
        message.success('Upasarga artha created');
      }
      closeUaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setUaModalSaving(false);
    }
  }, [closeUaModal, reloadDetailIfOpen, uaEditing, uaForm, uaHeadTingantaId]);

  const handleDeleteUpasargaArtha = useCallback(
    async (id: number) => {
      try {
        await deleteUpasargaArtha(id);
        message.success('Upasarga artha deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const tingantaArthaColumns: ColumnsType<TingantaArtha> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Language',
        key: 'lang',
        width: 160,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      { title: 'Text', dataIndex: 'text', ellipsis: true },
      {
        title: 'Actions',
        key: 'taActions',
        width: 160,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openTaEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this tinganta artha?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteTingantaArtha(r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteTingantaArtha, languageRows, openTaEdit]
  );

  const languageSelectOptions = useMemo(
    () => languageRows.map((l) => ({ value: l.id, label: `${l.name} (${l.code})` })),
    [languageRows]
  );

  const bidhaOpts = useMemo(() => enumToOptions(ARTHA_BIDHA), []);
  const prakarOpts = useMemo(() => enumToOptions(SUBANTA_SHRENI), []);
  const upasargaSelectOptions = useMemo(
    () =>
      [...upasargaList].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ).map((u) => ({ value: u.id, label: `${u.name} (#${u.id})` })),
    [upasargaList]
  );

  const upasargaArthaColumns: ColumnsType<UpasargaArtha> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Language',
        key: 'lang',
        width: 140,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      {
        title: 'Upasargas (order)',
        key: 'upa',
        width: 200,
        ellipsis: true,
        render: (_, r) => formatUpasargaIdList(r.upasargaIds, upasargaList),
      },
      { title: 'Artha', dataIndex: 'artha', ellipsis: true },
      { title: 'Bidha', dataIndex: 'bidha', width: 100, ellipsis: true },
      { title: 'Prakār', dataIndex: 'prakar', width: 90, ellipsis: true },
      {
        title: 'Actions',
        key: 'uaActions',
        width: 160,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openUaEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this upasarga artha?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteUpasargaArtha(r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteUpasargaArtha, languageRows, openUaEdit, upasargaList]
  );

  const requiredStr = (label: string) => [
    { required: true, message: `${label} is required` },
  ];

  const columns: ColumnsType<Tinganta> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Dhātu', dataIndex: 'dhatu', width: 100, ellipsis: true },
    { title: 'Aupadeśik', dataIndex: 'aupadeshik', width: 120, ellipsis: true },
    { title: 'Gaṇa', dataIndex: 'gana', width: 100, ellipsis: true },
    { title: 'Pada', dataIndex: 'pada', width: 130, ellipsis: true },
    { title: 'Artha', dataIndex: 'artha', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void openDetail(record.id)}
          >
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this tinganta?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Tinganta"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add tinganta
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        CRUD for <code>/tinganta</code> (verb forms) on the dictionary API (
        {process.env.REACT_APP_DICTIONARY_API_URL || '—'}).
      </Typography.Paragraph>
      <Table<Tinganta>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 960 }}
      />

      <Modal
        title="Tinganta details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
        width={920}
        styles={{ body: { maxHeight: '85vh', overflowY: 'auto' } }}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            {TINGANTA_SCALAR_KEYS.map((key) => (
              <Typography.Paragraph key={key} style={{ marginBottom: 4 }}>
                <Typography.Text strong>{key}:</Typography.Text> {detail[key] || '—'}
              </Typography.Paragraph>
            ))}

            <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              Tiṅganta arthas (per language)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              <code>GET/POST /tinganta-artha</code> — one row per language for this entry.
            </Typography.Paragraph>
            <Space style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openTaCreate(detail.id)}
              >
                Add tinganta artha
              </Button>
            </Space>
            {tingantaArthaRows.length > 0 ? (
              <Table<TingantaArtha>
                size="small"
                rowKey="id"
                loading={tingantaArthasLoading}
                columns={tingantaArthaColumns}
                dataSource={tingantaArthaRows}
                pagination={false}
                scroll={{ x: 560 }}
              />
            ) : (
              <Typography.Paragraph type="secondary">
                {tingantaArthasLoading ? 'Loading…' : 'none'}
              </Typography.Paragraph>
            )}

            <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              Upasarga artha (prefix chains)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              <code>GET/POST /upasarga-artha</code> — meanings per language and ordered{' '}
              <code>upasargaIds</code>. Sequence matters: <code>[1, 4]</code> ≠ <code>[4, 1]</code>.
            </Typography.Paragraph>
            <Space style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openUaCreate(detail.id)}
              >
                Add upasarga artha
              </Button>
            </Space>
            {upasargaArthaRows.length > 0 ? (
              <Table<UpasargaArtha>
                size="small"
                rowKey="id"
                loading={upasargaArthasLoading}
                columns={upasargaArthaColumns}
                dataSource={upasargaArthaRows}
                pagination={false}
                scroll={{ x: 960 }}
              />
            ) : (
              <Typography.Paragraph type="secondary">
                {upasargaArthasLoading ? 'Loading…' : 'none'}
              </Typography.Paragraph>
            )}

            <TingantaRupSections tingantaId={detail.id} detail={detail} onReload={reloadDetailIfOpen} />

            <EntryMediaPanel
              variant="tinganta"
              parentId={detail.id}
              rawMedia={detail.media}
              onReload={reloadDetailIfOpen}
            />

            <EntryStatsPanel
              variant="tinganta"
              parentId={detail.id}
              rawStats={detail.stats}
              onReload={reloadDetailIfOpen}
            />

            <EntryNotificationsPanel
              variant="tinganta"
              parentId={detail.id}
              rawNotifications={detail.notifications}
              onReload={reloadDetailIfOpen}
            />
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? 'Edit tinganta' : 'Add tinganta'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        destroyOnClose
        width={720}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Core
          </Typography.Title>
          <Form.Item name="i" label="i" rules={requiredStr('i')}>
            <Input placeholder="e.g. 1" />
          </Form.Item>
          <Form.Item name="baseindex" label="baseindex" rules={requiredStr('baseindex')}>
            <Input placeholder="e.g. 1.1" />
          </Form.Item>
          <Form.Item name="dhatu" label="dhātu" rules={requiredStr('dhātu')}>
            <Input placeholder="e.g. gam" />
          </Form.Item>
          <Form.Item name="aupadeshik" label="aupadeśik" rules={requiredStr('aupadeśik')}>
            <Input placeholder="e.g. गम्" />
          </Form.Item>
          <Form.Item name="gana" label="gaṇa" rules={requiredStr('gaṇa')}>
            <Input placeholder="e.g. bhvadi" />
          </Form.Item>
          <Form.Item name="pada" label="pada" rules={requiredStr('pada')}>
            <Input placeholder="e.g. parasmaipada" />
          </Form.Item>
          <Form.Item name="settva" label="settva" rules={requiredStr('settva')}>
            <Input placeholder="e.g. set" />
          </Form.Item>
          <Form.Item name="karma" label="karma" rules={requiredStr('karma')}>
            <Input placeholder="e.g. sakarmaka" />
          </Form.Item>
          <Form.Item name="artha" label="artha" rules={requiredStr('artha')}>
            <Input placeholder="e.g. to go" />
          </Form.Item>
          <Form.Item
            name="artha_english"
            label="artha_english"
            rules={requiredStr('artha_english')}
          >
            <Input placeholder="e.g. to go" />
          </Form.Item>

          <Typography.Title level={5}>Notes and references</Typography.Title>
          <Form.Item name="tags" label="tags">
            <Input placeholder="e.g. movement,verb" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini" label="dhaturoopnandini">
            <Input placeholder="Reference" />
          </Form.Item>
          <Form.Item name="sk" label="sk">
            <Input />
          </Form.Item>
          <Form.Item name="lsk_sutra" label="lsk_sutra">
            <Input placeholder="Lsk sutra ref" />
          </Form.Item>
          <Form.Item name="sk_sutra" label="sk_sutra">
            <Input placeholder="Sk sutra ref" />
          </Form.Item>
          <Form.Item name="artha_hindi" label="artha_hindi">
            <Input placeholder="e.g. जाना" />
          </Form.Item>
          <Form.Item name="antarganas" label="antarganas">
            <Input placeholder="e.g. sub-gana" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini_note" label="dhaturoopnandini_note">
            <Input.TextArea rows={2} placeholder="Note" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={taEditing ? 'Edit tinganta artha' : 'Add tinganta artha'}
        open={taModalOpen}
        onCancel={closeTaModal}
        onOk={() => void submitTaModal()}
        confirmLoading={taModalSaving}
        destroyOnClose
        width={520}
      >
        <Form form={taForm} layout="vertical" preserve={false}>
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select
              options={languageSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
            />
          </Form.Item>
          <Form.Item
            name="text"
            label="Text"
            rules={[{ required: true, message: 'Text is required' }]}
          >
            <Input.TextArea rows={4} placeholder="Meaning or translation" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={uaEditing ? 'Edit upasarga artha' : 'Add upasarga artha'}
        open={uaModalOpen}
        onCancel={closeUaModal}
        onOk={() => void submitUaModal()}
        confirmLoading={uaModalSaving}
        destroyOnClose
        width={640}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <Form form={uaForm} layout="vertical" preserve={false}>
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
          >
            <Select
              options={languageSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select language"
            />
          </Form.Item>
          <Form.Item
            name="upasargaIds"
            label="Upasargas (order)"
            rules={[
              { required: true, type: 'array', min: 1, message: 'Select at least one upasarga' },
            ]}
            extra="Select prefixes in the order they attach to the dhātu (first selected = outer/first in chain)."
          >
            <Select
              mode="multiple"
              options={upasargaSelectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select one or more, in order"
            />
          </Form.Item>
          <Form.Item
            name="artha"
            label="Artha"
            rules={[{ required: true, message: 'Artha is required' }]}
          >
            <Input.TextArea rows={3} placeholder="Meaning text" />
          </Form.Item>
          <Form.Item name="example" label="Example">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="antonyms" label="Antonyms">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="synonyms" label="Synonyms">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="tags" label="Tags">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="bidha" label="Bidha">
            <Select
              allowClear
              options={bidhaOpts}
              showSearch
              optionFilterProp="label"
              placeholder="Optional"
            />
          </Form.Item>
          <Form.Item name="prakar" label="Prakār (śreṇi)">
            <Select
              allowClear
              options={prakarOpts}
              showSearch
              optionFilterProp="label"
              placeholder="Optional"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
